<script lang="ts">
    import { browser } from '$app/env'
    import { resolve } from '$app/paths'
    import {
        listCachedRepos,
        deleteRepo,
        clearAll,
        getTotalSize,
        formatBytes,
        type CachedRepoInfo,
    } from '#lib/cache.js'
    import { parseRepoUrl } from '#lib/url.js'

    interface Props {
        id?: string
    }

    let { id }: Props = $props()

    let repos = $state<CachedRepoInfo[]>([])
    let totalSize = $state(0)
    let expanded = $state(false)

    const load = async () => {
        if (!browser) return
        repos = await listCachedRepos()
        totalSize = await getTotalSize()
    }

    $effect(() => {
        load()
        window.addEventListener('cache-changed', load)
        return () => window.removeEventListener('cache-changed', load)
    })

    const handleDelete = async (repoUrl: string) => {
        await deleteRepo(repoUrl)
    }

    const handleClearAll = async () => {
        await clearAll()
    }

    const formatDate = (iso: string): string => {
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            })
        } catch {
            return iso
        }
    }

    const shortName = (url: string): string => {
        try {
            const parsed = new URL(url)
            return parsed.pathname.replace(/^\//, '').replace(/\.git$/, '')
        } catch {
            return url
        }
    }

    const repoPath = (repoUrl: string): string => {
        try {
            const parsed = parseRepoUrl(repoUrl)
            return `${parsed.host}/${parsed.owner}/${parsed.repo}`
        } catch {
            return ''
        }
    }
</script>

{#if repos.length > 0}
    <div {id} class="mx-auto max-w-2xl">
        <button
            onclick={() => (expanded = !expanded)}
            aria-expanded={expanded}
            class="flex cursor-pointer items-center gap-2 text-foreground-tertiary transition-colors
				hover:text-foreground-secondary"
            style="font-family: var(--font-mono); font-size: 0.75rem; letter-spacing: 0.02em; transition-duration: var(--duration-fast);"
        >
            <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
                class="transition-transform duration-200"
                style:transform={expanded ? 'rotate(90deg)' : 'rotate(0deg)'}
            >
                <polyline points="9 18 15 12 9 6" />
            </svg>
            {expanded ? 'hide' : 'manage'} cached repos ({repos.length}, {formatBytes(totalSize)})
        </button>

        {#if expanded}
            <div class="strata-card strata-fade-in mt-3 p-4 text-left">
                <div class="mb-3 flex items-center justify-between">
                    <p
                        class="text-foreground"
                        style="font-family: var(--font-sans); font-size: 0.875rem; font-weight: 500;"
                    >
                        Cached repos
                        <span
                            class="font-normal text-foreground-tertiary"
                            style="font-family: var(--font-mono); font-size: 0.75rem;"
                        >
                            ({formatBytes(totalSize)})
                        </span>
                    </p>
                    <button
                        onclick={handleClearAll}
                        class="text-xs text-error transition-colors hover:underline"
                        style="font-family: var(--font-mono); letter-spacing: 0.02em; transition-duration: var(--duration-fast);"
                    >
                        clear all
                    </button>
                </div>
                <ul class="space-y-2">
                    {#each repos as repo (repo.repoUrl)}
                        <li class="flex items-center justify-between gap-3">
                            <div class="min-w-0 flex-1">
                                <a
                                    href="{resolve('/')}{repoPath(repo.repoUrl)}"
                                    class="block truncate text-foreground hover:text-accent hover:underline transition-colors"
                                    style="font-family: var(--font-mono); font-size: 0.875rem; text-decoration: none; transition-duration: var(--duration-fast);"
                                >
                                    {shortName(repo.repoUrl)}
                                </a>
                                <p
                                    class="text-foreground-tertiary"
                                    style="font-family: var(--font-mono); font-size: 0.75rem; letter-spacing: 0.02em;"
                                >
                                    {formatDate(repo.analyzedAt)} / {formatBytes(repo.sizeBytes)}
                                </p>
                            </div>
                            <button
                                onclick={() => handleDelete(repo.repoUrl)}
                                class="btn-ghost shrink-0 px-2 py-1 text-xs"
                                aria-label={`Delete cached data for ${shortName(repo.repoUrl)}`}
                            >
                                Delete
                            </button>
                        </li>
                    {/each}
                </ul>
            </div>
        {/if}
    </div>
{/if}
